import React from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import parse from 'html-react-parser'

import ParkingTags from '@/components/parking/ParkingTags'
import { FeatureTypes, Parking } from '@/service'
import { Text2 } from '@/styles/typography'
import PriceTag from '@/components/parkings/PriceTag'
import { useIsMobile } from '@/utils/useMobile'
import { useIsTablet } from '@/utils/useIsTablet'

import { colors } from '@/styles/theme'
import { useMarket } from '@/utils/multi-markets/context'

import CommonButton from '@/components/form/Button/CommonButton'

import BtnGlobalCustomStyles from '@/components/form/Button/button.customStyles'

import { getNavigationIcon } from '@/constants/navigationAssets'

import { useFetchMinimalReviews } from '@/service/apiHooks/useFetchMinimalReviews'

import getFacilities from '@/utils/getFacilities'
import { selectParkingDisplayPrice } from '@/utils/selectParkingDisplayPrice'

import {
  TileContainer,
  ImageContainer,
  InfoContainer,
  PriceAndButton,
  PriceWrapper,
  StyledImageWrapper,
  ParkingTitle,
  DoubleRow,
  TagsWrapper,
  LowestPriceWrapper,
  CrossedText2,
  ParkingMetaSection,
  ParkingRoutingSection,
  FacilityItem,
  SegmentedInformationSection,
  DiscountDescription
} from './ParkingTile.styled'

import Rating from './ParkingTile/Rating'
import LastBooking from './ParkingTile/LastBooking'
import RoutingBox from './ParkingTile/Routing'

interface ParkingTileProps {
  name: string
  description: string
  imageUrl: string
  onClick: (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
  ) => void
  parking: Parking
  dataTestId: string
  onMapModalOptn: () => void
}

const ParkingTile: React.FC<ParkingTileProps> = ({
  name,
  description,
  imageUrl,
  onClick,
  parking,
  dataTestId,
  onMapModalOptn
}) => {
  const router = useRouter()
  const { locale } = useMarket()
  const { reviewData, isLoading } = useFetchMinimalReviews({
    key: parking.urlSlug,
    locale
  })
  const handleRedirectToParkingPage = () => {
    router.push({
      pathname: '/parkings/[city]/[slug]',
      query: {
        city: parking.cityUrlSlug,
        slug: parking.urlSlug,
        startDate: router.query.startDate,
        endDate: router.query.endDate
      }
    })
  }
  const { isMobile } = useIsMobile()
  const { isTablet } = useIsTablet()

  const { t } = useTranslation(['parkings', 'common'])

  const translatedParkingTileDetails = t(
    'parkings:searchNearParking.parkingTile.details'
  )
  const translatedParkingTileQuickBook = t(
    'parkings:searchNearParking.parkingTile.quickBooking'
  )
  const translatedDistanceLabel = t('common:distance.from').split(' ')[0]

  const isQuickButtonDisabled =
    parking.subscriptionAvailable === false &&
    parking.reservationAvailable === false

  const isParkAndFly = parking.infoTags?.includes('PARK_AND_FLY')

  const selectedDisplayPrice = selectParkingDisplayPrice(parking.price)

  const shouldHighlightHourlyPrice =
    selectedDisplayPrice?.source === 'lastPricePerHour'

  const shouldDisplayLowestHourlyPrice =
    Boolean(parking.price?.lowestLast30daysPricePerHour) &&
    selectedDisplayPrice?.periodInDays === undefined

  const formattedPrice =
    locale === 'ar'
      ? new Intl.NumberFormat('ar-EG').format(
          parking?.price?.lowestLast30daysPricePerHour?.amount
        )
      : parking?.price?.lowestLast30daysPricePerHour?.amount

  const currencyText = t(
    `common:currency${parking.price?.lowestLast30daysPricePerHour?.currency}` as any
  )

  const formatDistance = (distanceInMeters: number) => {
    625
    if (!distanceInMeters && distanceInMeters !== 0) return null

    const distanceInKm = distanceInMeters / 1000
    return `${distanceInKm
      .toFixed(1)
      .replace('.', ',')} ${translatedDistanceLabel}`
  }

  const distanceText = formatDistance(parking.calculatedDistanceInMeters)

  const facilities = getFacilities(parking?.parkingFeatures ?? {})

  const isDesktop = !isMobile && !isTablet

  return (
    <TileContainer
      data-test-id={dataTestId}
      onClick={handleRedirectToParkingPage}
    >
      <ImageContainer data-test-id="image-container">
        <StyledImageWrapper data-test-id="image-wrapper">
          <img
            src={imageUrl}
            alt={name}
            width={164}
            height={164}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </StyledImageWrapper>
      </ImageContainer>
      <InfoContainer data-test-id="info-container">
        <SegmentedInformationSection>
          {(!parking?.lastReservation && !reviewData?.totalReviews) ||
          (!isDesktop && !parking?.lastReservation) ? (
            <></>
          ) : (
            <ParkingMetaSection>
              {parking?.lastReservation ? (
                <LastBooking value={parking?.lastReservation} />
              ) : (
                <div />
              )}
              {!isLoading && isDesktop && (
                <Rating
                  reviews={reviewData?.totalReviews}
                  rating={reviewData?.rating}
                />
              )}
            </ParkingMetaSection>
          )}
          <ParkingTitle data-test-id="parking-title">
            {t(`parkings:${name}` as any)}
          </ParkingTitle>
          {isTablet && (
            <TagsWrapper data-test-id="tablet-tags-wrapper">
              <ParkingTags
                parking={parking}
                short
                positionAbsolute={false}
                noMargin={true}
              />
            </TagsWrapper>
          )}
          <CommonButton
            variant="tileText"
            onClick={e => {
              e.stopPropagation()
              onMapModalOptn()
            }}
            data-test-id="details-button"
            customStyle={BtnGlobalCustomStyles.bookingDetail}
          >
            {t('parkings:searchNearParking.filters.mapButton')}
          </CommonButton>
        </SegmentedInformationSection>
        <DoubleRow>
          <ParkingRoutingSection multipleLines>
            {facilities?.slice(0, !isDesktop ? 3 : 10).map(facility => (
              <FacilityItem seperated={isMobile} key={String(facility)}>
                <>
                  <img src={getNavigationIcon('checkTick')} alt="check icon" />
                  {t(`parkings:facility.${facility as FeatureTypes}`)}
                </>
              </FacilityItem>
            ))}
          </ParkingRoutingSection>
          <DiscountDescription>
            {shouldDisplayLowestHourlyPrice && (
              <LowestPriceWrapper>
                <Text2 style={{ fontSize: 14 }}>
                  {parse(t('common:lastMonthLowestPrice'))}
                </Text2>
                <CrossedText2>
                  {t('common:priceFrom')} {formattedPrice} {currencyText}
                </CrossedText2>
              </LowestPriceWrapper>
            )}
          </DiscountDescription>
        </DoubleRow>
        <DoubleRow>
          <div>
            <ParkingRoutingSection>
              {isParkAndFly ? (
                <>
                  {parking?.parkingFeatures?.distanceToAirportInKilometers && (
                    <RoutingBox
                      icon="plane"
                      distance={
                        parking?.parkingFeatures
                          ?.distanceToAirportInKilometers + ' km'
                      }
                      description={
                        isDesktop ? t('parkings:tile.fromAirport') : ''
                      }
                    />
                  )}
                  {parking?.parkingFeatures?.transferTimeInMinutes && (
                    <RoutingBox
                      icon="car"
                      distance={
                        parking?.parkingFeatures?.transferTimeInMinutes + ' min'
                      }
                      description={
                        isDesktop ? t('parkings:tile.tansferTime') : ''
                      }
                    />
                  )}
                </>
              ) : distanceText ? (
                <RoutingBox
                  icon="arrow"
                  distance={distanceText}
                  description={
                    isDesktop ? t('parkings:tile.fromDestination') : ''
                  }
                />
              ) : (
                <></>
              )}
            </ParkingRoutingSection>
          </div>
          <div>
            {isDesktop && selectedDisplayPrice && (
              <PriceWrapper
                shouldHighlightPrice={shouldHighlightHourlyPrice}
                data-test-id="price-wrapper"
              >
                <PriceTag
                  price={selectedDisplayPrice.price}
                  isAbsolute={false}
                  noMargin
                  background="transparent"
                  color={
                    shouldHighlightHourlyPrice
                      ? colors.warningRed
                      : colors.twilightBlue
                  }
                  periodInDays={selectedDisplayPrice.periodInDays}
                />
              </PriceWrapper>
            )}
          </div>
        </DoubleRow>

        {!isDesktop && selectedDisplayPrice && (
          <TagsWrapper data-test-id="tags-wrapper">
            <PriceWrapper shouldHighlightPrice={shouldHighlightHourlyPrice}>
              <PriceTag
                price={selectedDisplayPrice.price}
                isAbsolute={false}
                noMargin
                background="transparent"
                color={
                  shouldHighlightHourlyPrice
                    ? colors.warningRed
                    : colors.twilightBlue
                }
                periodInDays={selectedDisplayPrice.periodInDays}
              />
            </PriceWrapper>
          </TagsWrapper>
        )}
        <DoubleRow data-test-id="button-row">
          {isDesktop ? (
            <ParkingTags
              parking={parking}
              short
              positionAbsolute={isMobile}
              noMargin={true}
            />
          ) : (
            <div />
          )}
          <PriceAndButton data-test-id="price-and-button">
            <CommonButton
              variant="searchWrapper"
              onClick={onClick}
              disabled={isQuickButtonDisabled}
              data-test-id="quick-book-button"
              customStyle={[
                BtnGlobalCustomStyles.treriary,
                BtnGlobalCustomStyles.treriaryLarge,
                isQuickButtonDisabled &&
                  BtnGlobalCustomStyles.disabledQuickBooking
              ]}
            >
              {t('parkings:tile.button.book')}
            </CommonButton>
          </PriceAndButton>
        </DoubleRow>
      </InfoContainer>
    </TileContainer>
  )
}

export default ParkingTile
